import {ref} from 'vue';
import type {NavbarConfig, NavbarItem} from '@navbar-unlogged-types';
import {isFilled, type LinkField, type PrismicDocument} from '@prismicio/client';
import type {NavbarMenuItemSlice} from '@prismicio-types';
import {usePrismicDocument} from '#imports';

const EMPTY_NAVBAR_CONFIG: NavbarConfig = {items: []};

export function useNavbarConfig() {
    const configsByDocumentId = ref<Record<string, NavbarConfig>>({});

    async function _loadPrismicNavbarConfig(navbarDocumentKey: string) {
        const {getDocumentViaAsyncData} = usePrismicDocument();
        const navbar = await getDocumentViaAsyncData('navbarMenu', navbarDocumentKey);

        return navbar;
    }

    async function loadNavbarConfigOnce(navbarDocumentKey: string): Promise<void> {
        const navbarDocument = await _loadPrismicNavbarConfig(navbarDocumentKey);

        if (!navbarDocument?.value) return;

        configsByDocumentId.value[navbarDocumentKey] = _prismicDocumentToNavbarConfig(navbarDocument.value as PrismicDocument);
    }

    async function loadAllNavbarConfigs(documentIds: string[]): Promise<void> {
        await Promise.all(documentIds.map((documentId) => loadNavbarConfigOnce(documentId)));
    }

    function getConfigForDocument(documentId: string | undefined | null): NavbarConfig {
        if (!documentId) return EMPTY_NAVBAR_CONFIG;
        return configsByDocumentId.value[documentId] ?? EMPTY_NAVBAR_CONFIG;
    }

    return {
        // refs a-z
        configsByDocumentId,
        // functions a-z
        getConfigForDocument,
        loadAllNavbarConfigs,
        loadNavbarConfigOnce,
    };
}

function _prismicDocumentToNavbarConfig(document: PrismicDocument): NavbarConfig {
    const items: NavbarItem[] = (document.data.slices ?? [])
        .map((slice: NavbarMenuItemSlice) => _prismicNavbarItemToNavbarConfig(slice))
        .filter((item: NavbarItem) => item != null);
    return {items};
}

function _prismicNavbarItemToNavbarConfig(prismicMenuItem: NavbarMenuItemSlice): NavbarItem | undefined {
    const labelField = prismicMenuItem.primary.label;
    if (!labelField?.text) return undefined;

    const navbarItem: NavbarItem = {
        label: labelField.text,
        items: [],
    };

    const featureCards = prismicMenuItem.primary.featureCard ?? [];
    if (featureCards.length > 0) {
        navbarItem.section = featureCards.map(({image, title, description, cta, backgroundColor}) => ({
            title: isFilled.keyText(title) ? title : undefined,
            description: isFilled.keyText(description) ? description : undefined,
            image: isFilled.image(image) && image.url ? image.url : undefined,
            imageAlt: isFilled.image(image) && image.alt ? image.alt : undefined,
            buttonText: isFilled.link(cta) && cta.text ? cta.text : undefined,
            buttonLink: isFilled.link(cta) && cta.url ? cta.url : undefined,
            backgroundColor: isFilled.select(backgroundColor) ? backgroundColor : undefined,
        }));
    }

    if (isFilled.link(labelField)) {
        navbarItem.href = labelField.url;
    }

    for (let i = 1; i <= 10; i++) {
        const prismicColumnLabel: LinkField = prismicMenuItem.primary[`column${i}Label` as keyof typeof prismicMenuItem.primary] as LinkField;
        if (prismicColumnLabel && prismicColumnLabel.text) {
            const columnItem: NavbarItem = {
                label: prismicColumnLabel.text,
                items: [],
            };

            // TODO fix/check href definition
            if (isFilled.link(prismicColumnLabel)) {
                columnItem.href = prismicColumnLabel.url;
            }

            const prismicColumnItems = prismicMenuItem.primary[`column${i}Items` as keyof typeof prismicMenuItem.primary];

            if (prismicColumnItems && Array.isArray(prismicColumnItems) && prismicColumnItems.length > 0) {
                for (const prismicColumnItem of prismicColumnItems as Array<{link: LinkField}>) {
                    if (isFilled.link(prismicColumnItem.link) && prismicColumnItem.link.text) {
                        columnItem.items!.push({
                            label: prismicColumnItem.link.text,
                            href: prismicColumnItem.link.url,
                        });
                    }
                }
            }

            navbarItem.items!.push(columnItem);
        }
    }

    return navbarItem;
}
